import { StrictMode } from 'react';
import { BrowserRouter } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';

import { ChromeOnlyGate } from '@/components/browser/ChromeOnlyGate';
import { AccessProvider } from '@/entities/user/providers/AccessProvider';
import { queryClient } from '@/library/react/queryClient';
import { HomePage } from '@/pages/HomePage';
import { ThemeProvider } from '@/providers/ThemeProvider';
import { AppRouter } from '@/router/AppRouter';

/**
 * Application provider shell.
 *
 * Deliberately WITHOUT HeroUI/NextUI — gpm-ui is self-sufficient and needs no
 * provider. AccessProvider (UG-4497) is the cookie-JWT session gate: it
 * resolves `POST /users/me`; a 401 renders the `fallback` (public login page),
 * a user renders the router with `useAccess()` available — so AppRouter only
 * ever renders authenticated content (RouteGuard adds per-route permissions).
 *
 *   <StrictMode>
 *     <QueryClientProvider>          server state (TanStack Query)
 *       <BrowserRouter>              react-router-dom v7 history
 *         <ThemeProvider>            light-locked gpm-ui tokens
 *           <AccessProvider fallback={<HomePage />}>   auth (cookie httpOnly JWT)
 *             <ChromeOnlyGate>       blocks the signed-in app outside Google Chrome (login stays public)
 *               <AppRouter />        UG-4498 routes + shell
 */
export function App() {
  return (
    <StrictMode>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <ThemeProvider>
            <AccessProvider fallback={<HomePage />}>
              <ChromeOnlyGate>
                <AppRouter />
              </ChromeOnlyGate>
            </AccessProvider>
          </ThemeProvider>
        </BrowserRouter>
        <ReactQueryDevtools initialIsOpen={false} />
      </QueryClientProvider>
    </StrictMode>
  );
}
